import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Alert,
  darken,
  IconButton,
  lighten,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  useTheme
} from '@mui/material'
import { AlertPhone, Valve, User, UserAlertRule } from '../types'
import { THEMES } from '../constants'
import { ExpandMore } from '@mui/icons-material'
import AlertRuleDataGrid from './AlertRuleDataGrid'
import { useAccordion, useDeviceAlertTemplates, useValveAlertTemplates } from '../hooks'
import { useEffect, useState } from 'react'
import { AlertGroup } from '../types/Alerts'
import { AxiosRequestConfig } from 'axios'
import LoadingState from './LoadingState'
import AlertRuleAddButton from './AlertRuleAddButton'
import AlertTemplateDefaultsButton from './AlertTemplateDefaultsButton'
import { useTranslation } from 'react-i18next'
import { getUserAlertRules, getUserAlertRulesByDevice } from '../services/userService'
import AlertPhoneAlertRulesDataGrid from './alertPhone/AlertPhoneAlertRulesDataGrid'
import DeviceStatusIndicator from './DeviceStatusIndicator'
import { getValveAlertRules } from '../services/valve'
import { getDeviceAlertRules } from '../services/deviceService'

interface ValveAlertRuleAccordionProps extends Omit<AccordionProps, 'children'> {
  valve: Valve
  user?: User | null
  alertPhone?: AlertPhone | null
  alertPhoneAlertRules?: UserAlertRule[]
}

export default function ValveAlertRuleAccordion({
  valve,
  user,
  alertPhone,
  alertPhoneAlertRules,
  sx,
  ...props
}: ValveAlertRuleAccordionProps) {
  if (!user && !alertPhone) {
    throw new Error('Must provide either "user" or "alertPhone" prop.')
  }

  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The theme function.
   */
  const theme = useTheme()

  /**
   * The accordion state.
   */
  const { expanded, toggleAccordion } = useAccordion()

  /**
   * The alert templates.
   */
  const valveAlertTemplates = useValveAlertTemplates()

  const deviceAlertTemplates = useDeviceAlertTemplates()

  const alertTemplates = [...valveAlertTemplates, ...deviceAlertTemplates]
  /**
   * The alert rules.
   */
  const [alertRules, setAlertRules] = useState<AlertGroup[]>([])

  /**
   * The user alert rules state.
   */
  const [userAlertRules, setUserAlertRules] = useState<UserAlertRule[]>([])

  /**
   * The enabled alert rules.
   */
  const enabledAlertRules = alertRules.filter((group) =>
    group.alerts.some((alert) => alert.enabled)
  )

  /**
   * The loading state.
   */
  const [isLoading, setLoading] = useState<boolean>(true)

  /**
   * The error state.
   */
  const [error, setError] = useState<unknown>()

  /**
   * Load the state.
   */
  async function loadStateForUser(userId: number, deviceName: string, config?: AxiosRequestConfig) {
    try {
      setLoading(true)
      await Promise.all([
        loadAlertRules(deviceName, config),
        loadUserAlertRules(userId, deviceName, config)
      ])
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the state.
   */
  async function loadStateForAlertPhone(
    alertPhoneId: number,
    deviceName: string,
    config?: AxiosRequestConfig
  ) {
    try {
      setLoading(true)
      await loadAlertRules(deviceName, config)
      setUserAlertRules(
        alertPhoneAlertRules?.filter(
          (rule) => rule?.alertPhoneId === alertPhoneId && rule.deviceName === deviceName
        ) ?? []
      )
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the alert rules.
   */
  async function loadAlertRules(name: string, config?: AxiosRequestConfig) {
    try {
      const deviceAlertRules = await getDeviceAlertRules(name, config)
      const valveAlertRules = await getValveAlertRules(name, config)
      setAlertRules([...deviceAlertRules, ...valveAlertRules])
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Load the user alert rules.
   */
  async function loadUserAlertRules(userId: number, name: string, config?: AxiosRequestConfig) {
    // setUserAlertRules(await getUserAlertRulesByDevice(userId, name, config))
    const userAlertRules = (await getUserAlertRules(userId, {}, config)).filter((rule) =>
      valve?.asset?.projectId
        ? rule.deviceName === name || rule.projectId === valve.asset.projectId
        : rule.deviceName === name
    )
    setUserAlertRules(userAlertRules)
  }

  /**
   * Load the state when the ruuvitag or the current user changes.
   */
  useEffect(() => {
    if (valve?.name) {
      const controller = new AbortController()

      if (user?.id) {
        loadStateForUser(user.id, valve.name, { signal: controller.signal })
      } else if (alertPhone?.id) {
        loadStateForAlertPhone(alertPhone.id, valve.name, { signal: controller.signal })
      }

      return () => controller.abort()
    }
  }, [valve.name, user?.id, alertPhone?.id])

  return (
    <Accordion
      expanded={expanded}
      sx={{
        ...sx,
        width: '100%',
        boxShadow: 0,
        '::before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          pl: 4,
          pr: 2,
          cursor: 'default',
          ':hover': {
            backgroundColor:
              theme.palette.mode === THEMES.DARK
                ? lighten(theme.palette.background.paper, 0.04)
                : darken(theme.palette.background.paper, 0.04)
          }
        }
      }}
      {...props}
    >
      <AccordionSummary
        onClick={toggleAccordion}
        expandIcon={
          <Tooltip title={expanded ? t('hideAlertRules') : t('showAlertRules')}>
            <IconButton>
              <ExpandMore />
            </IconButton>
          </Tooltip>
        }
      >
        <ListItem
          component="div"
          disablePadding
          sx={{
            p: 0,
            m: 0,
            width: '100%'
          }}
        >
          <ListItemIcon sx={{ minWidth: 24 }}>
            <DeviceStatusIndicator device={valve} />
          </ListItemIcon>
          <ListItemText
            sx={{ width: 'auto', pr: 0 }}
            primary={valve?.asset?.name ?? valve.name}
            secondary={valve.name}
            primaryTypographyProps={{
              sx: { order: '1 !important' }
            }}
            secondaryTypographyProps={{
              sx: { order: '2 !important' }
            }}
          />

          {!isLoading && expanded && !alertPhone && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              onClick={(event) => event.stopPropagation()}
              sx={{ mr: 1 }}
            >
              <AlertTemplateDefaultsButton
                alertTemplates={alertTemplates}
                alertRules={alertRules}
                device={valve}
                useRedux={false}
                afterUpdate={setAlertRules}
              />
              <AlertRuleAddButton
                alertTemplates={alertTemplates}
                alertRules={alertRules}
                device={valve}
                useRedux={false}
                afterUpdate={setAlertRules}
              />
            </Stack>
          )}
        </ListItem>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 8, py: 2 }}>
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <Alert severity="error">{t('loadAlertRulesError')}</Alert>
        ) : enabledAlertRules.length === 0 ? (
          <Alert severity="info">{t('noAlertRules')}</Alert>
        ) : alertPhone ? (
          <AlertPhoneAlertRulesDataGrid
            alertPhone={alertPhone}
            device={valve}
            alertPhoneAlertRules={userAlertRules}
            alertRules={alertRules}
            onUpdateAlertPhoneAlertRules={setUserAlertRules}
          />
        ) : (
          <AlertRuleDataGrid
            device={valve}
            project={valve?.asset?.project ?? undefined}
            alertTemplates={alertTemplates}
            alertRules={alertRules}
            userAlertRules={userAlertRules}
            useRedux={false}
            afterUpdate={setAlertRules}
            afterUserAlertRulesUpdate={setUserAlertRules}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}
